import { Box, Typography, IconButton, Tooltip } from '@mui/material';
import UploadFileIcon from '@mui/icons-material/UploadFile';
import FileDownloadIcon from '@mui/icons-material/FileDownload';
import VisibilityIcon from '@mui/icons-material/Visibility';
import DeleteIcon from '@mui/icons-material/Delete';
import EditIcon from '@mui/icons-material/Edit';
import { enqueueSnackbar } from 'notistack';
import { useEffect, useState } from 'react';
import { useAppDispatch } from '../../../../hooks/reduxHooks';
import { showAlert } from '../../../../features/slices/applicationSlice';
import { ALERT_CONFIRM_TEXT, ERROR_CONTACT_SUPPORT } from '../../../../constants/values';
import { useTranslation } from 'react-i18next';
import {
    useUploadActivityDocumentMutation,
    useDeleteActividadDocumentoMutation,
    useDownloadActividadDocumentoMutation,
    useDownloadConstanciaCapacitacionMutation,
    usePatchVisitaFechaVisitaMutation,
    useDownloadSeguimientoVerosimilitudPdfMutation
} from '../../../../services/visitasApi';
import BackdropComponent from '../../../../components/Backdrop/BackdropComponent';
import { downloadFromBase64 } from '../../../../utils/common';
import ActivitySummaryChip from './ActivitySummaryChip';
import { VISIT_TYPE_DISPLAY_NAME_OVERRIDES } from '../../consts/visitTypeDisplayNames';

const ROAM_ACTIVITY_TYPE_IDS = [467, 468, 474, 475, 476, 477, 478];
const ROAM_TEMPLATE_FILE_NAME = 'ANEXO I DE LA CIRCULAR G.P.y C. N° 00104.pdf';
const ROAM_TEMPLATE_FILE_PATH = `/docs/${encodeURIComponent(ROAM_TEMPLATE_FILE_NAME)}`;

export interface ActividadItem {
    id: number;
    estado?: number;
    tipoVisita: {
        id: number;
        descripcion: string;
        tiposArchivo?: {
            id: number;
            descripcion: string;
        }[];
    };
    documentos?: {
        id: number;
        fechaCarga: string;
        tipoArchivo: {
            id: number;
            descripcion: string;
        };
    }[];
}

interface TipoArchivo {
    id: number;
    descripcion: string;
}

interface TipoActividad {
    id: number;
    descripcion: string;
    tiposArchivo: TipoArchivo[];
}

interface Props {
    idVisita: number;
    actividad: ActividadItem;
    tipoActividad?: TipoActividad;
    refetchVisita: () => void;
    disableFunctionality?: boolean;
    isFailedVisit: boolean;
    shouldUpdateFechaVisitaOnDocumentUpload?: boolean;
    onCompletionChange?: (actividadId: number, isCompleted: boolean) => void;
    onOpenForm?: () => void;
    // El PDF del Seguimiento Verosimilitud sigue las reglas de descarga del Acta de visita
    // (SMP-353); lo resuelve la pantalla con el estado de la visita.
    canDownloadSeguimiento?: boolean;
    // El formulario del seguimiento se puede corregir después de enviado mientras la visita
    // siga editable (el backend acepta volver a guardarlo hasta que se carga la visita).
    canEditSeguimiento?: boolean;
}

export const ActivityActionsMenu = ({
    idVisita,
    actividad,
    tipoActividad,
    shouldUpdateFechaVisitaOnDocumentUpload = false,
    disableFunctionality = false,
    refetchVisita,
    onCompletionChange,
    isFailedVisit,
    onOpenForm,
    canDownloadSeguimiento = false,
    canEditSeguimiento = false
}: Props) => {
    const { t } = useTranslation('visitas');
    const dispatch = useAppDispatch();
    const [uploadDocument] = useUploadActivityDocumentMutation();
    const [patchVisitaFechaVisita] = usePatchVisitaFechaVisitaMutation();
    const [deleteDocument] = useDeleteActividadDocumentoMutation();
    const [uploadingId, setUploadingId] = useState<number | null>(null);
    const [downloadDocumento] = useDownloadActividadDocumentoMutation();
    const [isDeleting, setIsDeleting] = useState(false);
    const [isDownloading, setIsDownloading] = useState(false);
    const [downloadConstanciaCapacitacion, { isLoading: isDownloadingConstancia }] =
        useDownloadConstanciaCapacitacionMutation();
    const [downloadSeguimientoVerosimilitudPdf, { isLoading: isDownloadingSeguimiento }] =
        useDownloadSeguimientoVerosimilitudPdfMutation();

    const activityDescription = actividad.tipoVisita.descripcion.toUpperCase();
    const isCapacitacion = activityDescription.includes('CAPACITACI');
    const isRoamActivity = ROAM_ACTIVITY_TYPE_IDS.includes(actividad.tipoVisita.id);
    const isSeguimientoVerosimilitud = activityDescription.includes('SEGUIMIENTO VEROSIMILITUD');

    const handleSelectFile = async (e: any, idTipoArchivo: any) => {
        const file = e.target.files?.[0];
        if (!file) return;

        setUploadingId(idTipoArchivo);

        try {
            await uploadDocument({
                idVisita,
                idActividad: actividad.id,
                idTipoArchivo,
                file
            }).unwrap();

            if (shouldUpdateFechaVisitaOnDocumentUpload) {
                try {
                    await patchVisitaFechaVisita({
                        idVisita,
                        fechaVisita: new Date()
                    }).unwrap();
                } catch {
                    dispatch(
                        showAlert({
                            title: 'No se pudo actualizar la fecha de visita',
                            message: ERROR_CONTACT_SUPPORT,
                            icon: 'error',
                            confirmText: ALERT_CONFIRM_TEXT
                        })
                    );
                }
            }

            refetchVisita();
            enqueueSnackbar(t('execution.snackbar.uploadDocumentSuccess'), { variant: 'success' });
        } catch (err: any) {
            dispatch(
                showAlert({
                    title: 'No se pudo cargar el documento',
                    message: err?.data?.message || ERROR_CONTACT_SUPPORT,
                    icon: 'error',
                    confirmText: ALERT_CONFIRM_TEXT
                })
            );
        } finally {
            setUploadingId(null);
        }
    };

    const handleDownloadConstanciaCapacitacion = async () => {
        try {
            const response = await downloadConstanciaCapacitacion({
                idVisita
            }).unwrap();

            downloadFromBase64(response.data, idVisita, 'Constancia-Capacitacion');

            enqueueSnackbar(t('execution.snackbar.trainingCertificateDownloadSuccess'), {
                variant: 'success'
            });
        } catch (error: any) {
            dispatch(
                showAlert({
                    title: 'No se pudo descargar la constancia',
                    message: error?.data?.message || ERROR_CONTACT_SUPPORT,
                    icon: 'error',
                    confirmText: ALERT_CONFIRM_TEXT
                })
            );
        }
    };

    const handleDownloadRoamTemplate = () => {
        const link = document.createElement('a');
        link.href = ROAM_TEMPLATE_FILE_PATH;
        link.setAttribute('download', ROAM_TEMPLATE_FILE_NAME);
        document.body.appendChild(link);
        link.click();
        link.remove();
    };

    const handleDelete = async (idDocumento: number) => {
        setIsDeleting(true);
        try {
            await deleteDocument({
                idVisita,
                idActividad: actividad.id,
                idDocumento
            }).unwrap();
            refetchVisita();
            enqueueSnackbar(t('execution.snackbar.deleteDocumentSuccess'), { variant: 'success' });
        } catch (err: any) {
            dispatch(
                showAlert({
                    title: 'No se pudo eliminar el documento',
                    message: err?.data?.message || ERROR_CONTACT_SUPPORT,
                    icon: 'error',
                    confirmText: ALERT_CONFIRM_TEXT
                })
            );
        } finally {
            setIsDeleting(false);
        }
    };

    const handleDownloadSeguimiento = async () => {
        setIsDownloading(true);
        try {
            const response = await downloadSeguimientoVerosimilitudPdf({ idActividad: idVisita }).unwrap();
            if (response?.data) {
                const byteCharacters = atob(response.data);
                const byteNumbers = new Array(byteCharacters.length);
                for (let i = 0; i < byteCharacters.length; i++) {
                    byteNumbers[i] = byteCharacters.charCodeAt(i);
                }
                const byteArray = new Uint8Array(byteNumbers);
                const blob = new Blob([byteArray], { type: 'application/pdf' });
                const url = window.URL.createObjectURL(blob);
                const link = document.createElement('a');
                link.href = url;
                link.setAttribute('download', `Seguimiento-Verosimilitud.pdf`);
                document.body.appendChild(link);
                link.click();
                link.remove();
                window.URL.revokeObjectURL(url);
            }
        } catch {
            dispatch(
                showAlert({
                    title: 'No se pudo descargar el PDF de seguimiento',
                    message: ERROR_CONTACT_SUPPORT,
                    icon: 'error',
                    confirmText: ALERT_CONFIRM_TEXT
                })
            );
        } finally {
            setIsDownloading(false);
        }
    };

    const handleDownload = async (idDocumento: number) => {
        setIsDownloading(true);
        try {
            const response = await downloadDocumento({
                idVisita,
                idActividad: actividad.id,
                idDocumento
            }).unwrap();

            const url = response.data.presignedUrl;
            window.open(url, '_blank');
        } catch (e: any) {
            dispatch(
                showAlert({
                    title: 'No se pudo descargar el documento',
                    message: e?.data?.message || ERROR_CONTACT_SUPPORT,
                    icon: 'error',
                    confirmText: ALERT_CONFIRM_TEXT
                })
            );
        } finally {
            setIsDownloading(false);
        }
    };

    const isCompleted = isSeguimientoVerosimilitud
        ? actividad.estado === 2
        : (tipoActividad?.tiposArchivo?.every((tipo) =>
            actividad.documentos?.some((doc) => doc.tipoArchivo.id === tipo.id)
        ) ?? false);

    useEffect(() => {
        if (onCompletionChange) {
            onCompletionChange(actividad.id, isCompleted);
        }
    }, [actividad.id, isCompleted, onCompletionChange]);

    const hasRequiredDocuments = (tipoActividad?.tiposArchivo?.length ?? 0) > 0;

    if (!hasRequiredDocuments) {
        return null;
    }

    const canModifyDocuments = !disableFunctionality && !isFailedVisit;

    const activityDocumentTypesMap = (actividad.documentos ?? []).reduce<Record<number, string>>(
        (acc, doc) => {
            acc[doc.tipoArchivo.id] = doc.tipoArchivo.descripcion;
            return acc;
        },
        {}
    );

    return (
        <>
            <Box display="flex" width="100%" flexDirection="column">
                {tipoActividad?.tiposArchivo?.map((tipo: any) => {
                    const documentoSubido =
                        actividad.documentos?.find((doc) => doc.tipoArchivo?.id === tipo.id) ||
                        null;
                    const isDocCompleted = isSeguimientoVerosimilitud
                        ? actividad.estado === 2
                        : !!documentoSubido;
                    const activityLabel =
                        VISIT_TYPE_DISPLAY_NAME_OVERRIDES[actividad.tipoVisita.id] ??
                        actividad.tipoVisita.descripcion;
                    const documentLabel =
                        activityDocumentTypesMap[tipo.id] ?? tipo.descripcion ?? activityLabel;
                    const shouldShowDocumentLabel =
                        (tipoActividad?.tiposArchivo?.length ?? 0) > 1 &&
                        documentLabel !== activityLabel;

                    return (
                        <Box
                            key={tipo.id}
                            display="flex"
                            justifyContent="space-between"
                            alignItems="center"
                            width="100%"
                            sx={{ borderBottom: '1px solid #ccc', padding: '12px 8px' }}
                        >
                            <Box display="flex" alignItems="center" gap={2}>
                                <Box>
                                    <Typography
                                        sx={{
                                            fontSize: 14,
                                            fontWeight: 700,
                                            textTransform: 'uppercase'
                                        }}
                                    >
                                        {activityLabel}
                                    </Typography>
                                    {shouldShowDocumentLabel && (
                                        <Typography variant="body2" color="textSecondary">
                                            {documentLabel}
                                        </Typography>
                                    )}
                                </Box>

                                <ActivitySummaryChip status={isDocCompleted ? 'DONE' : 'PENDING'} />
                            </Box>

                            <Box
                                display="flex"
                                gap={1}
                                justifyContent="flex-end"
                                alignItems="center"
                                sx={{ mr: 2 }}
                            >
                                {isSeguimientoVerosimilitud
                                    ? actividad.estado === 2 && canDownloadSeguimiento && (
                                        <IconButton onClick={handleDownloadSeguimiento} disabled={isDownloadingSeguimiento}>
                                            <FileDownloadIcon />
                                        </IconButton>
                                    )
                                    : isDocCompleted && documentoSubido && (
                                        <IconButton onClick={() => handleDownload(documentoSubido.id)}>
                                            <VisibilityIcon />
                                        </IconButton>
                                    )
                                }

                                {isSeguimientoVerosimilitud &&
                                    onOpenForm &&
                                    (actividad.estado !== 2 || canEditSeguimiento) && (
                                    <Tooltip
                                        title={
                                            actividad.estado === 2
                                                ? 'Editar formulario de seguimiento'
                                                : 'Abrir formulario de seguimiento'
                                        }
                                    >
                                        <IconButton onClick={onOpenForm}>
                                            <EditIcon />
                                        </IconButton>
                                    </Tooltip>
                                )}

                                {canModifyDocuments &&
                                    !isSeguimientoVerosimilitud &&
                                    isDocCompleted &&
                                    documentoSubido && (
                                        <IconButton
                                            onClick={() => handleDelete(documentoSubido.id)}
                                        >
                                            <DeleteIcon />
                                        </IconButton>
                                    )}

                                {!isSeguimientoVerosimilitud &&
                                    !(isDocCompleted && documentoSubido) && (
                                        <>
                                            {isCapacitacion && (
                                                <Tooltip
                                                    title={t(
                                                        'execution.activities.downloadTrainingCertificate'
                                                    )}
                                                >
                                                    <IconButton
                                                        onClick={handleDownloadConstanciaCapacitacion}
                                                        disabled={isDownloadingConstancia}
                                                    >
                                                        <FileDownloadIcon />
                                                    </IconButton>
                                                </Tooltip>
                                            )}

                                            {isRoamActivity && (
                                                <Tooltip
                                                    title={t(
                                                        'execution.activities.downloadRoamTemplate'
                                                    )}
                                                >
                                                    <IconButton onClick={handleDownloadRoamTemplate}>
                                                        <FileDownloadIcon />
                                                    </IconButton>
                                                </Tooltip>
                                            )}

                                            {canModifyDocuments && (
                                                <IconButton component="label">
                                                    <UploadFileIcon />
                                                    <input
                                                        type="file"
                                                        hidden
                                                        accept="application/pdf"
                                                        onChange={(e) =>
                                                            handleSelectFile(e, tipo.id)
                                                        }
                                                    />
                                                </IconButton>
                                            )}
                                        </>
                                    )}
                            </Box>
                        </Box>
                    );
                })}
            </Box>
            <BackdropComponent
                loading={uploadingId !== null || isDeleting || isDownloading || isDownloadingSeguimiento}
                zIndex={2000}
            />
        </>
    );
};
